import {
  BrandTypes,
  FieldTypeAsset,
  FieldTypeLink,
} from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { sbEditable } from '@storyblok/storyblok-editable';
import { useRouter } from 'next/router';
import React from 'react';

import BrandHero from './BrandHero';
import BrandItem, { BrandItemProps } from './BrandItem';
import Button from '../button';

export interface BrandProps {
  brandType: BrandTypes;
  imageDesktop: FieldTypeAsset;
  imageMobile: FieldTypeAsset;
  headline?: string;
  subHeadline?: string;
  headlineButtonLabel?: string;
  headlineButtonLink?: FieldTypeLink;
  buttonLabel?: string;
  buttonLink?: FieldTypeLink;
  items?: BrandItemProps[];
  component: string;
  _uid: string;
  _editable?: string;
}

const Brand: React.FC<BrandProps> = (props: BrandProps) => {
  const {
    brandType,
    imageDesktop,
    imageMobile,
    headline,
    headlineButtonLabel,
    headlineButtonLink,
    subHeadline,
    buttonLabel,
    buttonLink,
    items,
  } = props;
  const router = useRouter();

  const handleButtonClick = () => {
    if (!buttonLink) return;
    const url = getUrlFromStoryblokLink(buttonLink);
    const openTab = buttonLink.target === '_blank';
    if (url) {
      const parsedUrl = parseSlashToURL(url);
      openTab ? window.open(parsedUrl, '_blank') : router.push(parsedUrl);
    }
  };

  const handleHeadlineButtonClick = () => {
    if (!headlineButtonLink) return;
    const url = getUrlFromStoryblokLink(headlineButtonLink);
    const openTab = headlineButtonLink.target === '_blank';
    if (url) {
      const parsedUrl = parseSlashToURL(url);
      openTab ? window.open(parsedUrl, '_blank') : router.push(parsedUrl);
    }
  };

  const classNames = {
    grid: 'grid grid-cols-24 mb-60px lg:mb-100px mt-60px lg:mt-100px',
    heroContainer: 'col-start-2 col-end-24',
    itemsContainer:
      'col-start-3 col-end-23 lg:grid lg:grid-cols-3 lg:gap-x-[10%]',
    buttonContainer:
      'col-start-3 col-end-23 lg:grid lg:grid-cols-3 lg:gap-x-[10%]',
    button: 'mt-40px w-full lg:col-start-2',
  };

  return (
    <div {...sbEditable(props)} className={classNames.grid}>
      <div className={classNames.heroContainer}>
        <BrandHero
          headline={headline}
          subline={subHeadline}
          buttonText={headlineButtonLabel}
          handleButtonClick={handleHeadlineButtonClick}
          imageDesktop={imageDesktop}
          imageMobile={imageMobile}
          brandType={brandType}
        />
      </div>
      <div className={classNames.itemsContainer}>
        {items?.map(({ icon, title, text }) => (
          <BrandItem key={title} icon={icon} title={title} text={text} />
        ))}
      </div>
      <div className={classNames.buttonContainer}>
        {buttonLabel && (
          <Button
            darkBg
            onClick={handleButtonClick}
            text={buttonLabel}
            customClassName={classNames.button}
          />
        )}
      </div>
    </div>
  );
};

export default Brand;
